import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import config from '../config';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

interface Character {
  _id: string;
  characterName: string;
  playerName: string;
  server: string;
  class: string;
  race: string;
  level: number;
  guild: string;
  createdAt: string;
  auth0Id: string;
  ownerName: string;
  ownerEmail: string;
}

const AdminCharacterList: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState<Character | null>(null);
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();

  // Only allow access for admin
  const isAdmin = user?.email === 'weezilla@gmail.com' && user?.sub?.startsWith('google-oauth2|');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchCharacters();
  }, [isAdmin, navigate]);

  const fetchCharacters = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${config.api.baseURL}/api/admin/characters`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCharacters(response.data);
    } catch (error) {
      console.error('Error fetching characters:', error);
      setError('Failed to fetch characters');
    }
  };

  const handleEdit = (character: Character) => {
    navigate(`/edit-character/${character._id}`, { state: { character } });
  };

  const handleDeleteClick = (character: Character) => {
    setCharacterToDelete(character);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!characterToDelete) return;

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${config.api.baseURL}/api/characters/${characterToDelete._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCharacters(characters.filter(c => c._id !== characterToDelete._id));
      setDeleteDialogOpen(false);
      setCharacterToDelete(null);
    } catch (error) {
      console.error('Error deleting character:', error);
      setError('Failed to delete character');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCharacterToDelete(null);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          variant="contained"
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5">Admin Character List</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Level</TableCell>
              <TableCell>Character Name</TableCell>
              <TableCell>Player Name</TableCell>
              <TableCell>Server</TableCell>
              <TableCell>Guild</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Race</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>OAuth ID</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {characters.map((character) => (
              <TableRow key={character._id}>
                <TableCell>{character.level}</TableCell>
                <TableCell>{character.characterName}</TableCell>
                <TableCell>{character.playerName}</TableCell>
                <TableCell>{character.server}</TableCell>
                <TableCell>{character.guild || '-'}</TableCell>
                <TableCell>{character.class}</TableCell>
                <TableCell>{character.race}</TableCell>
                <TableCell>
                  {new Date(character.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>{character.auth0Id}</TableCell>
                <TableCell>{character.ownerName}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      onClick={() => handleEdit(character)}
                      color="primary"
                      size="small"
                      title="Edit character"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(character)}
                      color="error"
                      size="small"
                      title="Delete character"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Character</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {characterToDelete?.characterName}?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCharacterList; 